import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { crmService } from '../../services/crmService';

// ─── Government Opportunities Component ────────────────────────────────────

function OpportunityCard({ opp, verticals, onAddToPipeline, onFollowOnCreated }) {
  const [expanded, setExpanded] = useState(false);
  const [showFollowOnForm, setShowFollowOnForm] = useState(false);
  const [followOns, setFollowOns] = useState([]);
  const [loadingFollowOns, setLoadingFollowOns] = useState(false);
  const [followOnData, setFollowOnData] = useState({
    title: '',
    agency: opp.agency,
    response_deadline: '',
    opportunity_value_usd: opp.opportunity_value_usd || 1000000,
  });
  const [followOnLoading, setFollowOnLoading] = useState(false);
  const [followOnError, setFollowOnError] = useState(null);

  useEffect(() => {
    if (expanded && opp.has_follow_ons && !opp.parent_opportunity_id) {
      setLoadingFollowOns(true);
      crmService.getFollowOnOpportunities(opp.id)
        .then(data => setFollowOns(data.follow_ons || []))
        .catch(console.error)
        .finally(() => setLoadingFollowOns(false));
    }
  }, [expanded, opp.id, opp.has_follow_ons, opp.parent_opportunity_id]);

  const daysUntilDeadline = Math.ceil((new Date(opp.response_deadline) - new Date()) / (1000*60*60*24));

  const deadlineColor = daysUntilDeadline < 3 ? '#EF4444' :
                        daysUntilDeadline < 7 ? '#F59E0B' : '#22D3EE';
  const deadlineLabel = daysUntilDeadline < 1 ? 'TODAY' :
                        daysUntilDeadline === 1 ? 'TOMORROW' :
                        `${daysUntilDeadline} days`;

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const handleFollowOnSubmit = async (e) => {
    e.preventDefault();
    setFollowOnLoading(true);
    setFollowOnError(null);

    try {
      if (!followOnData.title || !followOnData.agency || !followOnData.response_deadline) {
        throw new Error('Title, agency, and deadline are required');
      }

      await crmService.addFollowOnOpportunity(opp.id, {
        title: followOnData.title,
        agency: followOnData.agency,
        response_deadline: followOnData.response_deadline,
        opportunity_value_usd: followOnData.opportunity_value_usd,
        collaboration_partners: [],
      });

      setShowFollowOnForm(false);
      setFollowOnData({
        title: '',
        agency: opp.agency,
        response_deadline: '',
        opportunity_value_usd: opp.opportunity_value_usd || 1000000,
      });
      onFollowOnCreated?.();
    } catch (err) {
      setFollowOnError(err.message);
    } finally {
      setFollowOnLoading(false);
    }
  };

  return (
    <div style={{
      background: '#111827', border: `1px solid ${deadlineColor}33`, borderLeft: `4px solid ${deadlineColor}`,
      borderRadius: 8, padding: '14px 16px', marginBottom: 12,
      transition: 'all 0.2s'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ background: '#1E2D45', color: '#94A3B8', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
              {opp.opportunity_type || 'RFP'}
            </span>
            <h3 style={{ margin: 0, color: '#F1F5F9', fontSize: 14, fontWeight: 600 }}>
              {opp.title}
            </h3>
          </div>
          <p style={{ margin: '4px 0 0', color: '#64748B', fontSize: 11 }}>
            {opp.agency}
          </p>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ color: deadlineColor, fontWeight: 700, fontSize: 12 }}>
            {deadlineLabel}
          </div>
          <div style={{ color: '#94A3B8', fontSize: 10, marginTop: 2 }}>
            {formatDate(opp.response_deadline)}
          </div>
        </div>
      </div>

      {/* Metadata Row */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #1E2D45' }}>
        {opp.opportunity_value_usd && (
          <div>
            <span style={{ color: '#64748B', fontSize: 10 }}>Value:</span>
            <span style={{ color: '#22D3EE', fontSize: 11, fontWeight: 600, marginLeft: 4 }}>
              ${(opp.opportunity_value_usd / 1000000).toFixed(1)}M
            </span>
          </div>
        )}
        <div>
          <span style={{ color: '#64748B', fontSize: 10 }}>Posted:</span>
          <span style={{ color: '#94A3B8', fontSize: 11, marginLeft: 4 }}>
            {formatDate(opp.posted_date)}
          </span>
        </div>
        {opp.is_new_today && (
          <span style={{ background: 'rgba(34, 211, 238, 0.2)', color: '#22D3EE', padding: '2px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700 }}>
            NEW
          </span>
        )}
        {opp.parent_opportunity_id && (
          <span style={{ background: 'rgba(196, 165, 99, 0.2)', color: '#c4a563', padding: '2px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700 }}>
            🔗 FOLLOW-ON
          </span>
        )}
        {opp.has_follow_ons && (
          <span style={{ background: 'rgba(34, 211, 238, 0.2)', color: '#22D3EE', padding: '2px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700 }}>
            ⚡ HAS FOLLOW-ONS
          </span>
        )}
      </div>

      {/* Verticals & Organizations */}
      {(opp.matched_verticals?.length > 0 || opp.matched_organizations?.length > 0) && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          {opp.matched_verticals?.length > 0 && (
            <div>
              <div style={{ color: '#64748B', fontSize: 9, marginBottom: 4 }}>VERTICALS</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {opp.matched_verticals.slice(0, 3).map(v => (
                  <span key={v.id} style={{
                    background: '#22D3EE11', color: '#22D3EE', border: '1px solid #22D3EE33',
                    borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 600
                  }}>
                    {v.name}
                  </span>
                ))}
                {opp.matched_verticals.length > 3 && (
                  <span style={{ color: '#64748B', fontSize: 10 }}>+{opp.matched_verticals.length - 3}</span>
                )}
              </div>
            </div>
          )}
          {opp.matched_organizations?.length > 0 && (
            <div>
              <div style={{ color: '#64748B', fontSize: 9, marginBottom: 4 }}>AGENCIES</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {opp.matched_organizations.slice(0, 3).map(o => (
                  <span key={o.id} style={{
                    background: '#8B5CF611', color: '#A78BFA', border: '1px solid #8B5CF633',
                    borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 600
                  }}>
                    {o.name.split(' ')[0]}
                  </span>
                ))}
                {opp.matched_organizations.length > 3 && (
                  <span style={{ color: '#64748B', fontSize: 10 }}>+{opp.matched_organizations.length - 3}</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Expanded Detail */}
      {expanded && (opp.description || opp.naics_codes?.length > 0 || opp.has_follow_ons) && (
        <div style={{ background: '#0A0F1C', border: '1px solid #1E2D45', borderRadius: 4, padding: 12, marginBottom: 10 }}>
          {opp.description && (
            <p style={{ margin: 0, color: '#94A3B8', fontSize: 12, lineHeight: 1.6 }}>
              {opp.description}
            </p>
          )}
          {opp.naics_codes?.length > 0 && (
            <p style={{ margin: opp.description ? '8px 0 0' : 0, color: '#64748B', fontSize: 10 }}>
              <strong>NAICS:</strong> {opp.naics_codes.join(', ')}
            </p>
          )}
          {opp.has_follow_ons && !opp.parent_opportunity_id && (
            <div style={{ marginTop: opp.description || opp.naics_codes?.length > 0 ? 12 : 0, paddingTop: 12, borderTop: '1px solid #1E2D45' }}>
              <div style={{ color: '#c4a563', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>
                ⚡ Follow-On Opportunities ({followOns.length})
              </div>
              {loadingFollowOns ? (
                <div style={{ color: '#64748B', fontSize: 11 }}>Loading...</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {followOns.map(fo => (
                    <div key={fo.id} style={{
                      background: '#111827', border: '1px solid #c4a56333', borderRadius: 3,
                      padding: 8, fontSize: 10
                    }}>
                      <div style={{ color: '#F1F5F9', fontWeight: 600, marginBottom: 2 }}>
                        {fo.title}
                      </div>
                      <div style={{ color: '#64748B', marginBottom: 4 }}>
                        {fo.agency} • Deadline: {new Date(fo.response_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                      {fo.opportunity_value_usd && (
                        <div style={{ color: '#22D3EE', fontSize: 9 }}>
                          Value: ${(fo.opportunity_value_usd / 1000000).toFixed(1)}M
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            background: 'transparent', border: '1px solid #374151', borderRadius: 4,
            color: '#64748B', padding: '4px 8px', fontSize: 10, cursor: 'pointer'
          }}
        >
          {expanded ? 'Hide' : 'View'} Details
        </button>
        {opp.source_url && (
          <a
            href={opp.source_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: 'transparent', border: '1px solid #374151', borderRadius: 4,
              color: '#22D3EE', padding: '4px 8px', fontSize: 10, cursor: 'pointer',
              textDecoration: 'none'
            }}
          >
            View on SAM.gov ↗
          </a>
        )}
        {!opp.parent_opportunity_id && (
          <button
            onClick={() => setShowFollowOnForm(!showFollowOnForm)}
            style={{
              background: 'transparent', border: '1px solid #c4a563', borderRadius: 4,
              color: '#c4a563', padding: '4px 8px', fontSize: 10, fontWeight: 600, cursor: 'pointer'
            }}
          >
            ⚡ Add Follow-On
          </button>
        )}
        <button
          onClick={() => onAddToPipeline(opp)}
          style={{
            background: '#22D3EE', border: 'none', borderRadius: 4,
            color: '#0A0F1C', padding: '4px 10px', fontSize: 10, fontWeight: 600, cursor: 'pointer'
          }}
        >
          + Add to Pipeline
        </button>
      </div>

      {/* Follow-On Form */}
      {showFollowOnForm && (
        <div style={{ background: '#0A0F1C', border: '1px solid #c4a56333', borderRadius: 4, padding: 12, marginTop: 10 }}>
          <div style={{ color: '#c4a563', fontSize: 11, fontWeight: 600, marginBottom: 10 }}>
            ⚡ Create Follow-On Opportunity
          </div>
          <form onSubmit={handleFollowOnSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input
              type="text"
              placeholder="Follow-on title"
              value={followOnData.title}
              onChange={(e) => setFollowOnData(prev => ({ ...prev, title: e.target.value }))}
              style={{
                padding: '6px 10px', borderRadius: 4, background: '#111827', color: '#F1F5F9',
                border: '1px solid #1E2D45', fontSize: 11
              }}
              required
            />
            <input
              type="text"
              placeholder="Agency"
              value={followOnData.agency}
              onChange={(e) => setFollowOnData(prev => ({ ...prev, agency: e.target.value }))}
              style={{
                padding: '6px 10px', borderRadius: 4, background: '#111827', color: '#F1F5F9',
                border: '1px solid #1E2D45', fontSize: 11
              }}
              required
            />
            <input
              type="date"
              value={followOnData.response_deadline}
              onChange={(e) => setFollowOnData(prev => ({ ...prev, response_deadline: e.target.value }))}
              style={{
                padding: '6px 10px', borderRadius: 4, background: '#111827', color: '#F1F5F9',
                border: '1px solid #1E2D45', fontSize: 11
              }}
              required
            />
            <input
              type="number"
              placeholder="Value (USD)"
              value={followOnData.opportunity_value_usd}
              onChange={(e) => setFollowOnData(prev => ({ ...prev, opportunity_value_usd: parseFloat(e.target.value) || 0 }))}
              style={{
                padding: '6px 10px', borderRadius: 4, background: '#111827', color: '#F1F5F9',
                border: '1px solid #1E2D45', fontSize: 11
              }}
            />
            {followOnError && (
              <div style={{ color: '#EF4444', fontSize: 10 }}>
                {followOnError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={() => setShowFollowOnForm(false)}
                style={{
                  flex: 1, background: 'transparent', border: '1px solid #374151', borderRadius: 4,
                  color: '#64748B', padding: '6px 10px', fontSize: 10, cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={followOnLoading}
                style={{
                  flex: 1, background: '#c4a563', border: 'none', borderRadius: 4,
                  color: '#0A0F1C', padding: '6px 10px', fontSize: 10, fontWeight: 600,
                  cursor: followOnLoading ? 'not-allowed' : 'pointer', opacity: followOnLoading ? 0.7 : 1
                }}
              >
                {followOnLoading ? 'Creating...' : 'Create Follow-On'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default function GovernmentOpportunities() {
  const [opportunities, setOpportunities] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [filters, setFilters] = useState({
    vertical_id: '',
    agency: '',
    days_until_deadline: ''
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oppData, vertData] = await Promise.all([
        crmService.getGovOpportunities({ ...filters, limit: 100 }),
        crmService.getVerticals(),
      ]);
      setOpportunities(oppData.opportunities || []);
      setVerticals(vertData);
    } catch (e) {
      console.error('Load opportunities error:', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await crmService.syncGovOpportunities();
      await load(); // Refresh list
    } catch (e) {
      alert('Sync failed: ' + e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleAddToPipeline = (opp) => {
    // TODO: Implement create opportunity modal
    alert(`Add "${opp.title}" to pipeline\n\nImplement modal here with deadline: ${opp.response_deadline}`);
  };

  const stats = useMemo(() => {
    const now = new Date();
    return {
      total: opportunities.length,
      urgent: opportunities.filter(o => (new Date(o.response_deadline) - now) / (1000*60*60*24) < 3).length,
      highPriority: opportunities.filter(o => (new Date(o.response_deadline) - now) / (1000*60*60*24) < 7).length,
    };
  }, [opportunities]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#0A0F1C', color: '#F1F5F9' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #1E2D45', flexShrink: 0 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#22D3EE', marginBottom: 12 }}>
          🎯 Government Contracting Opportunities
        </h2>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <select
            value={filters.vertical_id}
            onChange={(e) => setFilters({ ...filters, vertical_id: e.target.value })}
            style={{
              padding: '6px 10px', borderRadius: 4, background: '#111827', color: '#F1F5F9',
              border: '1px solid #1E2D45', fontSize: 11
            }}
          >
            <option value="">All Verticals</option>
            {verticals.map(v => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Filter by agency..."
            value={filters.agency}
            onChange={(e) => setFilters({ ...filters, agency: e.target.value })}
            style={{
              padding: '6px 10px', borderRadius: 4, background: '#111827', color: '#F1F5F9',
              border: '1px solid #1E2D45', fontSize: 11, minWidth: 150
            }}
          />

          <select
            value={filters.days_until_deadline}
            onChange={(e) => setFilters({ ...filters, days_until_deadline: e.target.value })}
            style={{
              padding: '6px 10px', borderRadius: 4, background: '#111827', color: '#F1F5F9',
              border: '1px solid #1E2D45', fontSize: 11
            }}
          >
            <option value="">All Deadlines</option>
            <option value="7">Due within 7 days</option>
            <option value="14">Due within 14 days</option>
            <option value="30">Due within 30 days</option>
          </select>

          <button
            onClick={handleSync}
            disabled={syncing}
            style={{
              marginLeft: 'auto',
              background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4,
              padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
              opacity: syncing ? 0.7 : 1
            }}
          >
            {syncing ? 'Syncing…' : 'Refresh Opportunities'}
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 11 }}>
          <div>
            <span style={{ color: '#64748B' }}>Total:</span>
            <span style={{ color: '#F1F5F9', fontWeight: 600, marginLeft: 6 }}>{stats.total}</span>
          </div>
          <div>
            <span style={{ color: '#EF4444' }}>Urgent (less than 3 days):</span>
            <span style={{ color: '#EF4444', fontWeight: 600, marginLeft: 4 }}>{stats.urgent}</span>
          </div>
          <div>
            <span style={{ color: '#F59E0B' }}>High Priority (less than 7 days):</span>
            <span style={{ color: '#F59E0B', fontWeight: 600, marginLeft: 4 }}>{stats.highPriority}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {loading ? (
          <div style={{ color: '#64748B', textAlign: 'center', padding: '40px 20px' }}>
            Loading opportunities…
          </div>
        ) : opportunities.length === 0 ? (
          <div style={{ color: '#4B5563', textAlign: 'center', padding: '40px 20px' }}>
            No opportunities match your filters
          </div>
        ) : (
          opportunities.map(opp => (
            <OpportunityCard
              key={opp.id}
              opp={opp}
              verticals={verticals}
              onAddToPipeline={handleAddToPipeline}
              onFollowOnCreated={load}
            />
          ))
        )}
      </div>
    </div>
  );
}
